/**
 * useEmailSecuritySettings Hook
 * 
 * Manages email security settings for Total Connect module.
 * Settings are stored in client_config table with module_key = 'total-connect'
 * 
 * Usage:
 *   const { settings, loading, error, updateSettings, resetToDefaults } = useEmailSecuritySettings();
 */

import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

export interface BannerSettings {
  show_impersonation_warning: boolean;
  show_spam_warning: boolean;
  show_auth_failure_warning: boolean;
  show_unknown_sender_warning: boolean;
  show_first_time_sender_warning: boolean;
  show_external_sender_indicator: boolean;
  // Deep analysis / external sender banner customization
  show_deep_analysis_banner: boolean; // Whether to show total threat analysis banners at all
  external_banner_label: string; // Custom label after "Total Threat Analysis - " e.g. "External Sender" instead of "Suspicious Sender"
  external_banner_severity: 'info' | 'warning' | 'danger'; // Visual severity: info (blue/grey), warning (orange), danger (red)
}

export interface ContactMatchingSettings {
  check_crm_contacts: boolean;
  check_contact_people: boolean;
  check_internal_users: boolean;
  check_personal_contacts: boolean;
  trust_verified_contacts_only: boolean;
}

export interface ThresholdSettings {
  spam_warning_score: number;
  spam_block_score: number;
  impersonation_similarity: number;
}

export interface ActionSettings {
  auto_quarantine_high_spam: boolean;
  auto_quarantine_impersonation: boolean;
  auto_add_first_time_senders: boolean;
  notify_on_quarantine: boolean;
  allow_user_release: boolean;
}

export interface DeepAnalysisSettings {
  enabled: boolean;
  ai_enhanced_analysis: boolean;
  auto_scan_suspicious: boolean;
  scan_first_time_senders: boolean;
  check_ip_reputation: boolean;
  check_domain_reputation: boolean;
  check_bec_patterns: boolean;
  check_typosquatting: boolean;
}

export interface ListSettings {
  whitelisted_domains: string[];
  whitelisted_emails: string[];
  blacklisted_domains: string[];
  blacklisted_emails: string[];
}

export interface EmailSecuritySettings {
  banners: BannerSettings;
  contact_matching: ContactMatchingSettings;
  thresholds: ThresholdSettings;
  actions: ActionSettings;
  deep_analysis: DeepAnalysisSettings;
  lists: ListSettings;
}

// Default settings
export const DEFAULT_EMAIL_SECURITY_SETTINGS: EmailSecuritySettings = {
  banners: {
    show_impersonation_warning: true,
    show_spam_warning: true,
    show_auth_failure_warning: true,
    show_unknown_sender_warning: true,
    show_first_time_sender_warning: true,
    show_external_sender_indicator: true,
    show_deep_analysis_banner: true,
    external_banner_label: 'Suspicious Sender',
    external_banner_severity: 'warning',
  },
  contact_matching: {
    check_crm_contacts: true,
    check_contact_people: true,
    check_internal_users: true,
    check_personal_contacts: true,
    trust_verified_contacts_only: false,
  },
  thresholds: {
    spam_warning_score: 6,
    spam_block_score: 10,
    impersonation_similarity: 0.85,
  },
  actions: {
    auto_quarantine_high_spam: true,
    auto_quarantine_impersonation: false,
    auto_add_first_time_senders: false,
    notify_on_quarantine: true,
    allow_user_release: true,
  },
  deep_analysis: {
    enabled: true,
    ai_enhanced_analysis: true,
    auto_scan_suspicious: true,
    scan_first_time_senders: true,
    check_ip_reputation: true,
    check_domain_reputation: true,
    check_bec_patterns: true,
    check_typosquatting: true,
  },
  lists: {
    whitelisted_domains: [],
    whitelisted_emails: [],
    blacklisted_domains: [],
    blacklisted_emails: [],
  },
};

// ============================================================================
// HOOK
// ============================================================================

interface UseEmailSecuritySettingsResult {
  settings: EmailSecuritySettings | null;
  loading: boolean;
  error: string | null;
  updateSettings: (newSettings: Partial<EmailSecuritySettings>) => Promise<boolean>;
  updateSection: <K extends keyof EmailSecuritySettings>(
    section: K,
    data: Partial<EmailSecuritySettings[K]>
  ) => Promise<boolean>;
  resetToDefaults: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useEmailSecuritySettings(): UseEmailSecuritySettingsResult {
  const [settings, setSettings] = useState<EmailSecuritySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      
      const response = await fetch('/api/v1/connect-config?section=email_security', {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch settings');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Merge with defaults to ensure all fields exist
        const mergedSettings = deepMerge(DEFAULT_EMAIL_SECURITY_SETTINGS, data.email_security || {});
        setSettings(mergedSettings);
      } else {
        throw new Error(data.error || 'Failed to fetch settings');
      }
    } catch (err: any) {
      console.error('[useEmailSecuritySettings] Fetch error:', err);
      setError(err.message);
      // Use defaults on error
      setSettings(DEFAULT_EMAIL_SECURITY_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Update all settings
  const updateSettings = useCallback(async (newSettings: Partial<EmailSecuritySettings>): Promise<boolean> => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setError('Not authenticated');
        return false;
      }
      
      const response = await fetch('/api/v1/connect-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          email_security: { ...settings, ...newSettings },
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSettings(data.config?.email_security || deepMerge(settings!, newSettings));
        return true;
      } else {
        setError(data.error || 'Failed to update settings');
        return false;
      }
    } catch (err: any) {
      console.error('[useEmailSecuritySettings] Update error:', err);
      setError(err.message);
      return false;
    }
  }, [settings]);

  // Update specific section
  const updateSection = useCallback(async <K extends keyof EmailSecuritySettings>(
    section: K,
    data: Partial<EmailSecuritySettings[K]>
  ): Promise<boolean> => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setError('Not authenticated');
        return false;
      }
      
      const response = await fetch('/api/v1/connect-config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          section: 'email_security',
          data: {
            [section]: { ...(settings?.[section] || {}), ...data },
          },
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSettings(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            [section]: { ...prev[section], ...data },
          };
        });
        return true;
      } else {
        setError(result.error || 'Failed to update settings');
        return false;
      }
    } catch (err: any) {
      console.error('[useEmailSecuritySettings] Section update error:', err);
      setError(err.message);
      return false;
    }
  }, [settings]);

  // Reset to defaults
  const resetToDefaults = useCallback(async (): Promise<boolean> => {
    return updateSettings(DEFAULT_EMAIL_SECURITY_SETTINGS);
  }, [updateSettings]);

  return {
    settings,
    loading,
    error,
    updateSettings,
    updateSection,
    resetToDefaults,
    refresh: fetchSettings,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function deepMerge<T extends object>(target: T, source: Partial<T>): T {
  const result = { ...target };
  for (const key in source) {
    if (source[key] !== null && source[key] !== undefined) {
      if (typeof source[key] === 'object' && !Array.isArray(source[key])) {
        (result as any)[key] = deepMerge(
          (result as any)[key] || {},
          source[key] as object
        );
      } else {
        (result as any)[key] = source[key];
      }
    }
  }
  return result;
}

// ============================================================================
// STANDALONE FUNCTIONS (for use outside React components)
// ============================================================================

/**
 * Fetch email security settings (for use in non-React contexts)
 */
export async function fetchEmailSecuritySettings(): Promise<EmailSecuritySettings> {
  const tokens = authService.getTokens();
  if (!tokens?.access_token) {
    return DEFAULT_EMAIL_SECURITY_SETTINGS;
  }
  
  try {
    const response = await fetch('/api/v1/connect-config?section=email_security', {
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
      },
    });
    
    if (!response.ok) {
      return DEFAULT_EMAIL_SECURITY_SETTINGS;
    }
    
    const data = await response.json();
    
    if (data.success && data.email_security) {
      return deepMerge(DEFAULT_EMAIL_SECURITY_SETTINGS, data.email_security);
    }
    
    return DEFAULT_EMAIL_SECURITY_SETTINGS;
  } catch (err) {
    console.error('[fetchEmailSecuritySettings] Error:', err);
    return DEFAULT_EMAIL_SECURITY_SETTINGS;
  }
}

/**
 * Check if a sender should be trusted based on whitelist
 */
export function isSenderWhitelisted(
  email: string,
  settings: EmailSecuritySettings
): boolean {
  const emailLower = email.toLowerCase();
  const domain = emailLower.split('@')[1];
  
  // Check email whitelist
  if (settings.lists.whitelisted_emails.some(e => e.toLowerCase() === emailLower)) {
    return true;
  }
  
  // Check domain whitelist
  if (domain && settings.lists.whitelisted_domains.some(d => d.toLowerCase() === domain)) {
    return true;
  }
  
  return false;
}

/**
 * Check if a sender should be blocked based on blacklist
 */
export function isSenderBlacklisted(
  email: string,
  settings: EmailSecuritySettings
): boolean {
  const emailLower = email.toLowerCase();
  const domain = emailLower.split('@')[1];
  
  // Check email blacklist
  if (settings.lists.blacklisted_emails.some(e => e.toLowerCase() === emailLower)) {
    return true;
  }
  
  // Check domain blacklist
  if (domain && settings.lists.blacklisted_domains.some(d => d.toLowerCase() === domain)) {
    return true;
  }
  
  return false;
}

export default useEmailSecuritySettings;
